import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { trackPurchase, trackInitiateCheckout } from '../services/metaPixel';
import gtmService from '../services/gtmService';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  List,
  ListItem,
  Avatar,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CardGiftcard as GiftIcon,
  Store as StoreIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import CouponInput from './CouponInput';

// Helper functions for cart management
const getCartItems = () => {
  const cartStr = localStorage.getItem("cart");
  if (!cartStr) return [];
  try {
    return JSON.parse(cartStr);
  } catch (error) {
    console.error("Error parsing cart data:", error);
    return [];
  }
};

const saveCartItems = (items) => {
  localStorage.setItem("cart", JSON.stringify(items));
};

const getCurrentUser = () => {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;
  try {
    const user = JSON.parse(userStr);
    return { 
      id: user.id || null,
      name: user.name || user.username || '', 
      username: user.username || '',
      phone: user.phone || '',
      address: user.address || '',
      city: user.city || '',
      points: user.points || 0
    };
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

const Checkout = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [cartItems, setCartItems] = useState(getCartItems());
  const [user, setUser] = useState(getCurrentUser());
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // Removed loadingUserDetails state as refreshUserDetails function is commented out
  // const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const loadingUserDetails = false; // Always false since refresh function is not used
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Form states - Initialize with empty values first
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    phone: '',
    address: '',
    city: '',
    notes: ''
  });
  
  const [paymentMethod, setPaymentMethod] = useState('cash'); // cash, points
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderCode, setOrderCode] = useState(null);

  // Coupon states
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);

  // Phone verification states for non-users
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Timer for resend countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Check cart and initialize shipping info
  useEffect(() => {
    if (cartItems.length === 0 && !orderPlaced) {
      showMessage('السلة فارغة', 'warning');
      navigate('/store');
      return;
    }

    // Track checkout initiation with Meta Pixel
    trackInitiateCheckout(cartItems, false);

    // Initialize shipping info with stored user data (if user is logged in)
    if (user) {
      console.log('User data in checkout:', user); // Debug log
      console.log('User city from database:', user?.city); // Debug log
      console.log('Mapped city value:', mapCityToDropdownValue(user?.city)); // Debug log
      
      // Update shipping info whenever user data changes
      const mappedCity = mapCityToDropdownValue(user?.city);
      setShippingInfo(prevShippingInfo => ({
        fullName: user?.name || prevShippingInfo.fullName,
        phone: user?.phone || prevShippingInfo.phone,
        address: user?.address || prevShippingInfo.address,
        city: mappedCity || prevShippingInfo.city,
        notes: prevShippingInfo.notes // Keep existing notes
      }));
      
      console.log('Updated shipping info with user data'); // Debug log
    } else {
      console.log('Guest checkout mode - no user data to populate');
      // For guests, keep the shipping info empty for manual entry
      setShippingInfo(prevShippingInfo => ({
        fullName: prevShippingInfo.fullName || '',
        phone: prevShippingInfo.phone || '',
        address: prevShippingInfo.address || '',
        city: prevShippingInfo.city || '',
        notes: prevShippingInfo.notes || ''
      }));
    }
  }, [user, cartItems, orderPlaced, navigate]);

  // Refresh user details from database (optional function - only for logged in users)
  // Commented out - not currently used but kept for future implementation
  // const refreshUserDetails = async () => {
  //   if (!user) {
  //     showMessage('يجب تسجيل الدخول أولاً', 'warning');
  //     return;
  //   }
  //   
  //   setLoadingUserDetails(true);
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/user_details.php`, {
  //       method: 'GET',
  //       headers: {
  //         'Content-Type': 'application/json'
  //       },
  //       credentials: 'include'
  //     });
  //
  //     const responseText = await response.text();
  //     if (!responseText) {
  //       throw new Error('الخادم لم يرسل استجابة');
  //     }
  //     
  //     let result;
  //     try {
  //       result = JSON.parse(responseText);
  //     } catch (parseError) {
  //       console.error('JSON parse error:', parseError);
  //       console.error('Response text:', responseText);
  //       throw new Error('استجابة غير صالحة من الخادم');
  //     }
  //     
  //     if (response.ok && result.success) {
  //       const userData = result.user;
  //       console.log('Received user data from API:', userData);
  //       console.log('API Debug info:', result.debug);
  //       
  //       setUser(userData);
  //       
  //       setShippingInfo({
  //         fullName: userData.name || '',
  //         phone: userData.phone || '',
  //         address: userData.address || '',
  //         city: mapCityToDropdownValue(userData.city) || '',
  //         notes: shippingInfo.notes
  //       });
  //
  //       const updatedUserData = {
  //         id: userData.id,
  //         name: userData.name,
  //         username: userData.name || user.username,
  //         phone: userData.phone,
  //         address: userData.address,
  //         city: userData.city,
  //         role: user.role || 'customer',
  //         points: userData.points
  //       };
  //       localStorage.setItem('user', JSON.stringify(updatedUserData));
  //       
  //       showMessage('تم تحديث البيانات بنجاح', 'success');
  //     } else {
  //       showMessage('فشل في تحديث البيانات', 'error');
  //     }
  //     
  //   } catch (error) {
  //     console.error('Error refreshing user details:', error);
  //     showMessage('خطأ في تحديث البيانات', 'error');
  //   } finally {
  //     setLoadingUserDetails(false);
  //   }
  // };

  // Update shipping info and optionally save to localStorage
  const updateShippingInfo = (field, value) => {
    const newShippingInfo = { ...shippingInfo, [field]: value };
    setShippingInfo(newShippingInfo);
    
    // Reset phone verification if phone number changes for non-users
    if (field === 'phone' && !user) {
      setIsPhoneVerified(false);
      setVerificationCodeSent(false);
      setVerificationCode('');
      setResendTimer(0);
    }
    
    // Optional: Update localStorage user data when key fields change (only if user is logged in)
    if (user && ['fullName', 'phone', 'address', 'city'].includes(field)) {
      const updatedUser = {
        ...user,
        name: field === 'fullName' ? value : user?.name,
        phone: field === 'phone' ? value : user?.phone,
        address: field === 'address' ? value : user?.address,
        city: field === 'city' ? value : user?.city
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  // Helper function to map database city names to dropdown values
  const mapCityToDropdownValue = (cityName) => {
    if (!cityName) return '';
    
    const cityMapping = {
      // Arabic database values to English dropdown values
      'بغداد': 'Baghdad',
      'البصرة': 'Basra', 
      'أربيل': 'Erbil',
      'النجف': 'Najaf',
      'كربلاء': 'Karbala',
      'السليمانية': 'Sulaymaniyah',
      'الموصل': 'Mosul',
      'دهوك': 'Duhok',
      'بابل': 'Babylon',
      'كركوك': 'Kirkuk',
      'ذي قار': 'DhiQar',
      'المثنى': 'Muthanna',
      'القادسية': 'Qadisiyyah',
      'ميسان': 'Maysan',

      // Additional Iraqi cities
      'صلاح الدين': 'Saladin',
      'ديالى': 'Diyala',
      'الأنبار': 'Anbar',
      'واسط': 'Wasit',
      
      // Variations without الـ prefix
      'نجف': 'Najaf',
      'بصرة': 'Basra',
      'موصل': 'Mosul',
      'أنبار': 'Anbar',
      'مثنى': 'Muthanna',
      'قادسية': 'Qadisiyyah',
      
      // Keep English values as-is
      'Baghdad': 'Baghdad',
      'Basra': 'Basra',
      'Erbil': 'Erbil', 
      'Najaf': 'Najaf',
      'Karbala': 'Karbala',
      'Sulaymaniyah': 'Sulaymaniyah',
      'Mosul': 'Mosul',
      'Duhok': 'Duhok',
      'Babylon': 'Babylon',
      'Kirkuk': 'Kirkuk'
    };
    
    // Try exact match first
    if (cityMapping[cityName]) {
      return cityMapping[cityName];
    }
    
    // Try with trimmed spaces
    const trimmedCityName = cityName.trim();
    if (cityMapping[trimmedCityName]) {
      return cityMapping[trimmedCityName];
    }
    
    // If no match found, return the original value (will be handled as custom city)
    console.log('City not found in mapping:', cityName);
    return cityName;
  };

  // Helper function to convert English city names back to Arabic for display
  const mapCityToArabic = (cityName) => {
    if (!cityName) return '';
    
    const cityToArabicMapping = {
      // English to Arabic mapping
      'Baghdad': 'بغداد',
      'Basra': 'البصرة', 
      'Erbil': 'أربيل',
      'Najaf': 'النجف',
      'Karbala': 'كربلاء',
      'Sulaymaniyah': 'السليمانية',
      'Mosul': 'الموصل',
      'Duhok': 'دهوك',
      'Babylon': 'بابل',
      'Kirkuk': 'كركوك',
      'DhiQar': 'ذي قار',
      'Muthanna': 'المثنى',
      'Qadisiyyah': 'القادسية',
      'Maysan': 'ميسان',
      'Saladin': 'صلاح الدين',
      'Diyala': 'ديالى',
      'Anbar': 'الأنبار',
      'Wasit': 'واسط'
    };
    
    // If it's already in Arabic or has mapping, return the Arabic version
    if (cityToArabicMapping[cityName]) {
      return cityToArabicMapping[cityName];
    }
    
    // If no mapping found, return the original value (custom cities)
    return cityName;
  };

  // Helper functions
  const getItemPrice = (item) => {
    if (!item) return 0;
    
    // Handle gift items that have points_cost instead of price
    if (item.type === 'gift' && item.points_cost) {
      return Number(item.points_cost) || 0;
    }
    
    // Handle regular products - use the same logic as Store and Header components
    if (item.after_discount && Number(item.after_discount) > 0) {
      return Number(item.after_discount);
    }
    if (item.discount_price && Number(item.discount_price) > 0) {
      return Number(item.discount_price);
    }
    if (item.price && Number(item.price) > 0) {
      return Number(item.price);
    }
    return Number(item.sell_price || 0);
  };

  // Helper function to validate Iraqi phone numbers
  const validateIraqiPhone = (phone) => {
    if (!phone) return false;
    // Remove any spaces or dashes
    const cleanPhone = phone.replace(/[\s-]/g, '');
    // Check if it matches Iraqi phone format: 07XXXXXXXXX (11 digits starting with 07)
    const iraqiPhoneRegex = /^07\d{9}$/;
    return iraqiPhoneRegex.test(cleanPhone);
  };

  // Helper function to format phone number input
  const formatPhoneInput = (value) => {
    if (!value) return '';
    // Remove any non-digit characters
    let cleaned = value.replace(/\D/g, '');
    
    // Ensure it starts with 07
    if (cleaned.length > 0 && !cleaned.startsWith('07')) {
      if (cleaned.startsWith('0') && cleaned.length > 1) {
        cleaned = '07' + cleaned.substring(1);
      } else if (!cleaned.startsWith('0')) {
        cleaned = '07' + cleaned;
      }
    }
    
    // Limit to 11 digits
    if (cleaned.length > 11) {
      cleaned = cleaned.substring(0, 11);
    }
    
    return cleaned;
  };

  const getItemWeight = (item) => {
    if (!item) return 0;
    const weight = Number(item.weight) || 0;
    // If weight is 0, use a default weight of 1 for delivery cost calculation
    return weight > 0 ? weight : 1;
  };

  const getTotalWeight = () => {
    return cartItems.reduce((total, item) => {
      if (!item) return total;
      const weight = getItemWeight(item);
      const quantity = item.quantity || 0;
      return total + (weight * quantity);
    }, 0);
  };

  // Helper function to round delivery price to nearest 250 IQD
  const roundToNearest250 = (price) => {
    // Round to nearest 250
    // Examples: 5001-5249 → 5000, 5251-5499 → 5250, 5501-5749 → 5500, 5751-5999 → 5750
    return Math.round(price / 250) * 250;
  };

  const getDeliveryCost = () => {
    if (!hasRegularItems) return 0; // No delivery cost for gift-only orders
    const totalWeight = getTotalWeight();
    
    // Minimum delivery cost is 5000 IQD
    let deliveryCost = 5000;
    
    // If weight exceeds 5kg, add 1000 IQD per additional weight point
    if (totalWeight > 5) {
      const additionalWeight = totalWeight - 5;
      deliveryCost += additionalWeight * 1000;
    }
    
    // Round to nearest 250 IQD
    return roundToNearest250(deliveryCost);
  };

  const getRegularProductsTotal = () => {
    return cartItems.reduce((total, item) => {
      if (!item || item.type === 'gift') return total;
      const price = getItemPrice(item);
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  const getGiftsTotal = () => {
    return cartItems.reduce((total, item) => {
      if (!item || item.type !== 'gift') return total;
      const price = getItemPrice(item);
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  const getTotalCartItems = () => {
    return cartItems.reduce((total, item) => total + (item?.quantity || 0), 0);
  };

  // Coupon handlers
  const handleCouponApplied = (couponData) => {
    setAppliedCoupon(couponData.coupon);
    setCouponDiscount(couponData.discount.discount_amount);
  };

  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
  };

  const hasRegularItems = cartItems.some(item => item && item.type !== 'gift');
  const hasGiftItems = cartItems.some(item => item && item.type === 'gift');

  const showMessage = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Phone verification functions
  const sendVerificationCode = async () => {
    if (!shippingInfo.phone || !validateIraqiPhone(shippingInfo.phone)) {
      showMessage('يرجى إدخال رقم هاتف صحيح أولاً', 'error');
      return;
    }

    setVerificationLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout_whatsapp_verification.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send_code',
          phone: shippingInfo.phone,
          purpose: 'checkout_verification'
        }),
      });

      const responseText = await response.text();
      if (!responseText) {
        throw new Error('الخادم لم يرسل استجابة');
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response text:', responseText);
        throw new Error('استجابة غير صالحة من الخادم');
      }

      if (response.ok && result.success) {
        setVerificationCodeSent(true);
        setResendTimer(60); // 60 second cooldown
        showMessage('تم إرسال كود التحقق على واتساب', 'success');
      } else {
        // Handle specific WhatsApp API errors
        if (result.error && (result.error.includes('Error validating access token') || result.error.includes('session has been invalidated'))) {
          showMessage('خدمة واتساب غير متاحة حالياً. يرجى المحاولة مرة أخرى أو الاتصال بخدمة العملاء', 'error');
        } else {
          throw new Error(result.error || result.message || 'فشل في إرسال كود التحقق');
        }
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      // For network or server errors, show proper error message
      if (error.message.includes('Failed to fetch') || error.message.includes('network')) {
        showMessage('مشكلة في الاتصال بالخادم. يرجى التحقق من الإنترنت والمحاولة مرة أخرى', 'error');
      } else {
        showMessage(error.message || 'فشل في إرسال كود التحقق', 'error');
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      showMessage('يرجى إدخال كود التحقق المكون من 6 أرقام', 'error');
      return;
    }

    setVerificationLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout_whatsapp_verification.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verify_code',
          phone: shippingInfo.phone,
          code: verificationCode,
          purpose: 'checkout_verification'
        }),
      });

      const responseText = await response.text();
      if (!responseText) {
        throw new Error('الخادم لم يرسل استجابة');
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response text:', responseText);
        throw new Error('استجابة غير صالحة من الخادم');
      }

      if (response.ok && result.success) {
        setIsPhoneVerified(true);
        showMessage('تم تأكيد رقم الهاتف بنجاح', 'success');
        setActiveStep(prevStep => prevStep + 1); // Move to next step automatically
      } else {
        throw new Error(result.error || 'كود التحقق غير صحيح');
      }
    } catch (error) {
      console.error('Error verifying phone code:', error);
      showMessage(error.message || 'كود التحقق غير صحيح', 'error');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Cart management
  const updateCartItemQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const updatedItems = cartItems.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    
    setCartItems(updatedItems);
    saveCartItems(updatedItems);
  };

  const removeFromCart = (productId) => {
    const updatedItems = cartItems.filter(item => item.id !== productId);
    setCartItems(updatedItems);
    saveCartItems(updatedItems);
    
    if (updatedItems.length === 0 && !orderPlaced) {
      navigate('/store');
    }
  };

  // Stepper logic
  const steps = user ? 
    ['مراجعة الطلب', 'معلومات الشحن', 'طريقة الدفع', 'تأكيد الطلب'] :
    ['مراجعة الطلب', 'معلومات الشحن', 'طريقة الدفع', 'تأكيد الهاتف', 'تأكيد الطلب'];

  const handleNext = () => {
    if (activeStep === 1 && !validateShippingInfo()) {
      return;
    }
    
    if (activeStep === 2 && !validatePaymentMethod()) {
      return;
    }
    
    // Phone verification step for non-users
    if (!user && activeStep === 3 && !isPhoneVerified) {
      showMessage('يرجى تأكيد رقم الهاتف أولاً', 'error');
      return;
    }
    
    if ((user && activeStep === 3) || (!user && activeStep === 4)) {
      handlePlaceOrder();
      return;
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Validation functions
  const validateShippingInfo = () => {
    if (!shippingInfo.fullName.trim()) {
      showMessage('يرجى إدخال الاسم الكامل', 'error');
      return false;
    }
    if (!shippingInfo.phone.trim()) {
      showMessage('يرجى إدخال رقم الهاتف', 'error');
      return false;
    }
    if (!validateIraqiPhone(shippingInfo.phone)) {
      showMessage('يرجى إدخال رقم هاتف عراقي صحيح (07XXXXXXXXX)', 'error');
      return false;
    }
    if (!shippingInfo.address.trim()) {
      showMessage('يرجى إدخال العنوان', 'error');
      return false;
    }
    if (!shippingInfo.city.trim()) {
      showMessage('يرجى اختيار المدينة', 'error');
      return false;
    }
    return true;
  };

  const validatePaymentMethod = () => {
    if (hasGiftItems && !user) {
      showMessage('يجب تسجيل الدخول لشراء الهدايا باستخدام النقاط', 'error');
      return false;
    }
    if (hasGiftItems && user && getGiftsTotal() > (user?.points || 0)) {
      showMessage(`ليس لديك نقاط كافية. تحتاج إلى ${getGiftsTotal()} نقطة ولديك ${user?.points || 0} نقطة`, 'error');
      return false;
    }
    return true;
  };

  // Place order function
  const handlePlaceOrder = async () => {
    setLoading(true);
    
    try {
      // Prepare order data with calculated prices
      const itemsWithCalculatedPrices = cartItems.map(item => ({
        ...item,
        calculated_price: getItemPrice(item) // Add the calculated price to each item
      }));
      
      const orderData = {
        items: itemsWithCalculatedPrices,
        shipping: shippingInfo,
        payment_method: paymentMethod,
        user_id: user?.id || null, // Add user_id to the order data
        coupon: appliedCoupon, // Add coupon data
        totals: {
          regular_products: getRegularProductsTotal(),
          gifts: getGiftsTotal(),
          delivery_cost: hasRegularItems ? getDeliveryCost() : 0,
          coupon_discount: couponDiscount, // Add coupon discount
          total_weight: getTotalWeight(),
          total_items: getTotalCartItems()
        }
      };

      // Log order data being sent for debugging
      console.log('Sending order data:', JSON.stringify(orderData, null, 2));

      // Make API call to place order
      const response = await fetch(`${API_BASE_URL}/api/checkout.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Use session cookies instead of JWT
        body: JSON.stringify(orderData)
      });

      const responseText = await response.text();
      console.log('Checkout API response status:', response.status);
      console.log('Checkout API response text:', responseText);
      
      // Check for server errors (500, 503, etc)
      if (!response.ok) {
        // Try to parse error message from response
        if (responseText) {
          try {
            const errorData = JSON.parse(responseText);
            throw new Error(errorData.message || errorData.error || `خطأ في الخادم (${response.status})`);
          } catch (parseError) {
            // If response is not JSON, show the text or generic error
            if (responseText.includes('Fatal error') || responseText.includes('Parse error')) {
              console.error('PHP Error:', responseText);
              throw new Error('خطأ في معالجة الطلب. يرجى التواصل مع الدعم الفني');
            }
            throw new Error(`خطأ في الخادم (${response.status}): ${responseText.substring(0, 200)}`);
          }
        } else {
          throw new Error(`خطأ في الخادم (${response.status})`);
        }
      }
      
      if (!responseText) {
        throw new Error('الخادم لم يرسل استجابة');
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response text:', responseText);
        throw new Error('استجابة غير صالحة من الخادم: ' + responseText.substring(0, 100));
      }
      
      if (result.success) {
        setOrderId(result.order_id);
        setOrderCode(result.order_code); // Store the order code from backend
        setOrderPlaced(true);
        
        // Send WhatsApp confirmation to customer
        if (shippingInfo.phone && result.order_code) {
          try {
            console.log('Sending WhatsApp with data:', {
              phone: shippingInfo.phone,
              order_code: result.order_code
            });
            
            const whatsappResponse = await fetch(`${API_BASE_URL}/api/send_order_whatsapp.php`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                phone: shippingInfo.phone,
                order_code: result.order_code
              })
            });
            
            const whatsappText = await whatsappResponse.text();
            let whatsappResult;
            try {
              whatsappResult = whatsappText ? JSON.parse(whatsappText) : {};
            } catch (parseError) {
              console.error('WhatsApp JSON parse error:', parseError);
              console.error('WhatsApp response text:', whatsappText);
              whatsappResult = { success: false, error: 'Invalid response' };
            }
            console.log('WhatsApp API response:', whatsappResult);
            
            if (whatsappResult.success) {
              console.log('WhatsApp message sent successfully');
            } else {
              console.warn('WhatsApp send failed:', whatsappResult.error);
            }
          } catch (whatsappError) {
            console.log('WhatsApp notification failed (non-critical):', whatsappError);
          }
        } else {
          console.warn('Cannot send WhatsApp - missing phone or order_code:', {
            phone: shippingInfo.phone,
            order_code: result.order_code
          });
        }
        
        // Track purchase with Meta Pixel
        trackPurchase({
          order_id: result.order_id,
          items: itemsWithCalculatedPrices,
          total: orderData.totals.regular_products + orderData.totals.gifts + orderData.totals.delivery_cost,
          delivery_method: 'delivery', // Standard delivery for regular checkout
          payment_method: paymentMethod
        }, false);
        
        // Track purchase with Google Tag Manager
        const totalValue = orderData.totals.regular_products + orderData.totals.gifts + orderData.totals.delivery_cost;
        gtmService.trackPurchase({
          transaction_id: result.order_code || result.order_id,
          value: totalValue,
          currency: 'SAR',
          items: itemsWithCalculatedPrices.map(item => ({
            item_id: item.id,
            item_name: item.name,
            category: item.category || 'General',
            quantity: item.quantity,
            price: getItemPrice(item)
          }))
        });
        
        // Clear cart
        setCartItems([]);
        saveCartItems([]);
        
        // Update user points if gifts were purchased and user is logged in
        if (hasGiftItems && user) {
          const updatedUser = { ...user, points: (user?.points || 0) - getGiftsTotal() };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        }
        
        showMessage('تم تقديم الطلب بنجاح! سيتم إرسال تفاصيل الطلب على واتساب', 'success');
        setActiveStep(user ? 4 : 5); // Success step (different for users vs non-users)
        
      } else {
        throw new Error(result.message || 'حدث خطأ في تقديم الطلب');
      }
      
    } catch (error) {
      console.error('Order placement error:', error);
      showMessage(error.message || 'حدث خطأ في تقديم الطلب', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Print receipt function
  const handlePrintReceipt = () => {
    if (!orderId && !orderCode) {
      showMessage('معرف الطلب غير متاح', 'error');
      return;
    }
    
    // Use order code if available, otherwise fall back to order ID
    const orderIdentifier = orderCode || orderId;
    const printUrl = `${API_BASE_URL}/api/order_pdf.php?order_code=${encodeURIComponent(orderIdentifier)}`;
    
    // Open PDF in a new tab for printing
    window.open(printUrl, '_blank');
    showMessage('جاري فتح الفاتورة للطباعة...', 'info');
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderCartReview();
      case 1:
        return renderShippingForm();
      case 2:
        return renderPaymentMethod();
      case 3:
        // For logged-in users, show order confirmation directly
        // For non-users, show phone verification
        return user ? renderOrderConfirmation() : renderPhoneVerification();
      case 4:
        // For logged-in users, show order success
        // For non-users, show order confirmation
        return user ? renderOrderSuccess() : renderOrderConfirmation();
      case 5:
        // Only for non-users: order success
        return renderOrderSuccess();
      default:
        return null;
    }
  };

  // Cart Review Step
  const renderCartReview = () => (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CartIcon sx={{ mr: 1 }} />
        مراجعة الطلب ({getTotalCartItems()} {getTotalCartItems() === 1 ? 'منتج' : 'منتجات'})
      </Typography>
      
      <List>
        {cartItems.map((item, index) => (
          <React.Fragment key={item.id}>
            <ListItem sx={{ px: 0, py: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' }, 
                width: '100%',
                gap: { xs: 2, sm: 0 }
              }}>
                {/* Product Image and Info */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexGrow: 1,
                  minWidth: 0 // Allows shrinking
                }}>
                  <Avatar
                    src={item.image && typeof item.image === 'string' && item.image.trim() ? 
                      (item.image.startsWith('http') ? 
                        item.image : 
                        item.image.startsWith('api/uploads/products/') ? 
                          `${API_BASE_URL}/${item.image}` : 
                          `https://al-sadik.com/api/uploads/products/${item.image}`
                      ) : '/placeholder-image.png'
                    }
                    sx={{ 
                      width: { xs: 50, sm: 60 }, 
                      height: { xs: 50, sm: 60 }, 
                      mr: 2,
                      flexShrink: 0
                    }}
                    variant="rounded"
                  >
                    {item.type === 'gift' ? <GiftIcon /> : <StoreIcon />}
                  </Avatar>
                  
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        lineHeight: 1.2,
                        wordBreak: 'break-word'
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        mt: 0.5
                      }}
                    >
                      {(getItemPrice(item) || 0).toLocaleString()} {item.type === 'gift' ? 'نقطة للقطعة' : 'د.ع للقطعة'}
                    </Typography>
                    {item.type === 'gift' && (
                      <Chip
                        icon={<GiftIcon />}
                        label="هدية"
                        size="small"
                        color="secondary"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </Box>
                
                {/* Quantity Controls and Price - Mobile: Stack, Desktop: Row */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'row', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: { xs: 'space-between', sm: 'flex-end' },
                  gap: { xs: 1, sm: 2 },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  {/* Quantity Controls */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper'
                  }}>
                    <IconButton 
                      size="small"
                      onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                      sx={{ padding: { xs: '4px', sm: '8px' } }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ 
                      mx: { xs: 1, sm: 2 }, 
                      minWidth: { xs: 20, sm: 30 }, 
                      textAlign: 'center',
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}>
                      {item.quantity}
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                      sx={{ padding: { xs: '4px', sm: '8px' } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Total Price */}
                  <Box sx={{ 
                    textAlign: 'right', 
                    minWidth: { xs: 80, sm: 100 }
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}
                    >
                      {((getItemPrice(item) || 0) * (item.quantity || 1)).toLocaleString()}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {item.type === 'gift' ? 'نقطة' : 'د.ع'}
                    </Typography>
                  </Box>
                  
                  {/* Delete Button */}
                  <IconButton 
                    onClick={() => removeFromCart(item.id)}
                    size="small"
                    sx={{ 
                      ml: { xs: 0.5, sm: 1 },
                      color: 'error.main'
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </ListItem>
            {index < cartItems.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Coupon Input */}
      {hasRegularItems && (
        <CouponInput
          cartItems={cartItems.filter(item => item.type !== 'gift')}
          onCouponApplied={handleCouponApplied}
          onCouponRemoved={handleCouponRemoved}
          userId={user?.id}
          isGiftShop={false}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Subtotal */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1">المجموع الفرعي:</Typography>
          <Box sx={{ textAlign: 'right' }}>
            {hasGiftItems && hasRegularItems ? (
              <Typography variant="body1">
                {getRegularProductsTotal().toLocaleString()} د.ع + {getGiftsTotal().toLocaleString()} نقطة
              </Typography>
            ) : (
              <Typography variant="body1">
                {(getRegularProductsTotal() + getGiftsTotal()).toLocaleString()} {hasGiftItems ? 'نقطة' : 'د.ع'}
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* Delivery Cost (only for regular products) */}
        {hasRegularItems && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              تكلفة التوصيل:
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {getDeliveryCost().toLocaleString()} د.ع
            </Typography>
          </Box>
        )}
        
        {/* Coupon Discount (only for regular products) */}
        {hasRegularItems && couponDiscount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" color="success.main">
              خصم الكوبون ({appliedCoupon?.code}):
            </Typography>
            <Typography variant="body1" color="success.main">
              -{couponDiscount.toLocaleString()} د.ع
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">الإجمالي:</Typography>
          <Box sx={{ textAlign: 'right' }}>
            {hasGiftItems && hasRegularItems ? (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {(getRegularProductsTotal() - couponDiscount + getDeliveryCost() + getGiftsTotal()).toLocaleString()} وحدة
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(getRegularProductsTotal() - couponDiscount + getDeliveryCost()).toLocaleString()} د.ع + {getGiftsTotal().toLocaleString()} نقطة
                </Typography>
              </Box>
            ) : hasRegularItems ? (
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {(getRegularProductsTotal() - couponDiscount + getDeliveryCost()).toLocaleString()} د.ع
              </Typography>
            ) : (
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {getGiftsTotal().toLocaleString()} نقطة
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  // Shipping Form Step
  const renderShippingForm = () => (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon sx={{ mr: 1 }} />
          معلومات الشحن
        </Typography>
      </Box>
      
      {!user && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            🔒 طلب كزائر (غير مسجل)
          </Typography>
          <Typography variant="body2">
            يرجى ملء جميع البيانات المطلوبة بعناية. لن نتمكن من حفظ هذه البيانات لطلبات مستقبلية.
          </Typography>
          <Button 
            size="small" 
            onClick={() => navigate('/login')} 
            sx={{ mt: 1 }}
          >
            تسجيل الدخول لحفظ البيانات
          </Button>
        </Alert>
      )}
      
      {user && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            👤 مرحباً <strong>{user?.name || 'المستخدم'}</strong>! تم ملء البيانات من حسابك تلقائياً.
          </Typography>
        </Alert>
      )}
      
      {loadingUserDetails && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="الاسم الكامل"
            value={shippingInfo.fullName}
            onChange={(e) => updateShippingInfo('fullName', e.target.value)}
            required
            disabled={loadingUserDetails}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="رقم الهاتف"
            value={shippingInfo.phone}
            onChange={(e) => {
              const formattedPhone = formatPhoneInput(e.target.value);
              updateShippingInfo('phone', formattedPhone);
            }}
            required
            disabled={loadingUserDetails}
            placeholder="07XXXXXXXXX"
            helperText="مثال: 07801234567"
            inputProps={{
              maxLength: 11,
              inputMode: 'numeric'
            }}
            error={shippingInfo.phone && !validateIraqiPhone(shippingInfo.phone)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="العنوان التفصيلي"
            value={shippingInfo.address}
            onChange={(e) => updateShippingInfo('address', e.target.value)}
            multiline
            rows={2}
            required
            disabled={loadingUserDetails}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required disabled={loadingUserDetails}>
            <InputLabel>المدينة</InputLabel>
            <Select
              value={shippingInfo.city}
              onChange={(e) => updateShippingInfo('city', e.target.value)}
              label="المدينة"
            >
              <MenuItem value="Baghdad">بغداد</MenuItem>
              <MenuItem value="Basra">البصرة</MenuItem>
              <MenuItem value="Erbil">أربيل</MenuItem>
              <MenuItem value="Najaf">النجف</MenuItem>
              <MenuItem value="Karbala">كربلاء</MenuItem>
              <MenuItem value="Sulaymaniyah">السليمانية</MenuItem>
              <MenuItem value="Mosul">الموصل</MenuItem>
              <MenuItem value="Duhok">دهوك</MenuItem>
              <MenuItem value="Babylon">بابل</MenuItem>
              <MenuItem value="Kirkuk">كركوك</MenuItem>
              <MenuItem value="DhiQar">ذي قار</MenuItem>
              <MenuItem value="Muthanna">المثنى</MenuItem>
              <MenuItem value="Qadisiyyah">القادسية</MenuItem>
              <MenuItem value="Maysan">ميسان</MenuItem>
              <MenuItem value="Saladin">صلاح الدين</MenuItem>
              <MenuItem value="Diyala">ديالى</MenuItem>
              <MenuItem value="Anbar">الأنبار</MenuItem>
              <MenuItem value="Wasit">واسط</MenuItem>
              <MenuItem value="Other">مدينة أخرى</MenuItem>
            </Select>
          </FormControl>
          {/* Show text field for custom city if "Other" is selected or city doesn't match dropdown */}
          {(shippingInfo.city === 'Other' || (shippingInfo.city && !['Baghdad', 'Basra', 'Erbil', 'Najaf', 'Karbala', 'Sulaymaniyah', 'Mosul', 'Duhok', 'Babylon', 'Kirkuk', 'DhiQar', 'Muthanna', 'Qadisiyyah', 'Maysan', 'Saladin', 'Diyala', 'Anbar', 'Wasit', 'Other'].includes(shippingInfo.city))) && (
            <TextField
              fullWidth
              label="اسم المدينة"
              value={shippingInfo.city === 'Other' ? '' : shippingInfo.city}
              onChange={(e) => updateShippingInfo('city', e.target.value)}
              sx={{ mt: 2 }}
              placeholder="أدخل اسم المدينة"
              disabled={loadingUserDetails}
              helperText={shippingInfo.city !== 'Other' ? 'المدينة المحفوظة في قاعدة البيانات' : ''}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="ملاحظات إضافية (اختياري)"
            value={shippingInfo.notes}
            onChange={(e) => updateShippingInfo('notes', e.target.value)}
            multiline
            rows={2}
            disabled={loadingUserDetails}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  // Payment Method Step
  const renderPaymentMethod = () => (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <PaymentIcon sx={{ mr: 1 }} />
        طريقة الدفع
      </Typography>
      
      <FormControl component="fieldset">
        <RadioGroup
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          <FormControlLabel 
            value="cash" 
            control={<Radio />} 
            label="الدفع عند التسليم" 
            disabled={hasGiftItems && !hasRegularItems}
          />
        </RadioGroup>
      </FormControl>
      
      {!user && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            🔒 <strong>زائر (غير مسجل)</strong>
          </Typography>
          <Typography variant="body2">
            يمكنك إتمام الطلب كزائر، لكن لن تتمكن من تتبع طلباتك.
          </Typography>
          <Typography variant="body2">
            <Button 
              size="small" 
              onClick={() => navigate('/login')} 
              sx={{ mt: 1 }}
            >
              تسجيل الدخول للحصول على المزايا
            </Button>
          </Typography>
        </Alert>
      )}
    </Paper>
  );

  // Phone Verification Step (for non-users only)
  const renderPhoneVerification = () => (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <PersonIcon sx={{ mr: 1 }} />
        تأكيد رقم الهاتف
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          🔒 تأكيد الهوية للطلبات الخارجية
        </Typography>
        <Typography variant="body2">
          لضمان أمان طلبك، يرجى تأكيد رقم هاتفك عبر كود التحقق الذي سيتم إرساله على واتساب.
        </Typography>
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
          رقم الهاتف المراد تأكيده: <strong>{shippingInfo.phone}</strong>
        </Typography>
        
        {!verificationCodeSent ? (
          <Button
            variant="contained"
            onClick={sendVerificationCode}
            disabled={verificationLoading}
            size="large"
            sx={{ minWidth: 200 }}
          >
            {verificationLoading ? (
              <CircularProgress size={24} />
            ) : (
              'إرسال كود التحقق'
            )}
          </Button>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              تم إرسال كود التحقق على واتساب إلى الرقم {shippingInfo.phone}
            </Alert>
            
            <Box sx={{ mb: 3 }}>
              <TextField
                label="كود التحقق (6 أرقام)"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                sx={{ mr: 2, minWidth: 200 }}
                disabled={verificationLoading || isPhoneVerified}
              />
              
              <Button
                variant="contained"
                onClick={verifyPhoneCode}
                disabled={verificationLoading || verificationCode.length !== 6 || isPhoneVerified}
                size="large"
              >
                {verificationLoading ? (
                  <CircularProgress size={24} />
                ) : isPhoneVerified ? (
                  'تم التأكيد ✓'
                ) : (
                  'تأكيد الكود'
                )}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                لم تستلم الكود؟
              </Typography>
              <Button
                variant="text"
                onClick={sendVerificationCode}
                disabled={resendTimer > 0 || verificationLoading}
                size="small"
              >
                {resendTimer > 0 ? 
                  `إعادة الإرسال خلال ${resendTimer} ثانية` : 
                  'إعادة إرسال الكود'
                }
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {isPhoneVerified && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body2">
            ✅ تم تأكيد رقم الهاتف بنجاح! يمكنك الآن المتابعة لإتمام الطلب.
          </Typography>
        </Alert>
      )}
    </Paper>
  );

  // Order Confirmation Step
  const renderOrderConfirmation = () => (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CheckIcon sx={{ mr: 1 }} />
        تأكيد الطلب
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                معلومات الشحن
              </Typography>
              <Typography variant="body2"><strong>الاسم:</strong> {shippingInfo.fullName}</Typography>
              <Typography variant="body2"><strong>الهاتف:</strong> {shippingInfo.phone}</Typography>
              <Typography variant="body2"><strong>العنوان:</strong> {shippingInfo.address}</Typography>
              <Typography variant="body2"><strong>المدينة:</strong> {mapCityToArabic(shippingInfo.city)}</Typography>
              {shippingInfo.notes && (
                <Typography variant="body2"><strong>ملاحظات:</strong> {shippingInfo.notes}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                ملخص الطلب
              </Typography>
              <Typography variant="body2">
                <strong>عدد المنتجات:</strong> {getTotalCartItems()}
              </Typography>
              {hasRegularItems && (
                <>
                  <Typography variant="body2">
                    <strong>المنتجات العادية:</strong> {getRegularProductsTotal().toLocaleString()} د.ع
                  </Typography>
                  <Typography variant="body2">
                    <strong>تكلفة التوصيل:</strong> {getDeliveryCost().toLocaleString()} د.ع
                  </Typography>
                  {couponDiscount > 0 && (
                    <Typography variant="body2" color="success.main">
                      <strong>خصم الكوبون ({appliedCoupon?.code}):</strong> -{couponDiscount.toLocaleString()} د.ع
                    </Typography>
                  )}
                </>
              )}
              {hasGiftItems && (
                <Typography variant="body2">
                  <strong>الهدايا:</strong> {getGiftsTotal().toLocaleString()} نقطة
                </Typography>
              )}
              {hasRegularItems && (
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1 }}>
                  <strong>المجموع الكلي:</strong> {(getRegularProductsTotal() - couponDiscount + getDeliveryCost()).toLocaleString()} د.ع
                  {hasGiftItems && ` + ${getGiftsTotal().toLocaleString()} نقطة`}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>طريقة الدفع:</strong> {paymentMethod === 'cash' ? 'الدفع عند التسليم' : 'النقاط'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );

  // Order Success Step
  const renderOrderSuccess = () => (
    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '2px solid', borderColor: 'success.main', backgroundColor: 'white', borderRadius: 3 }}>
      <CheckIcon sx={{ fontSize: 100, color: 'success.main', mb: 3 }} />
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3, color: 'success.main' }}>
        تم تقديم طلبك بنجاح!
      </Typography>
      
      {/* Order Code Display */}
      <Box sx={{ 
        backgroundColor: '#f8f9fa', 
        border: '3px dashed', 
        borderColor: 'primary.main', 
        borderRadius: 2, 
        p: 3, 
        mb: 3,
        boxShadow: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
          كود الطلب: {orderCode || 'غير متاح'}
        </Typography>
        
        <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
          ستحتاج إلى كود الطلب هذا لمتابعة حالة طلبك والاستفسار عنه
        </Typography>
      </Box>

      {/* WhatsApp Notification Info */}
      <Alert severity="success" sx={{ mb: 3, textAlign: 'right' }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
          📱 تم إرسال تأكيد الطلب على واتساب
        </Typography>
        <Typography variant="body2">
          ستصلك رسالة على واتساب تحتوي على رقم طلبك وتفاصيل التوصيل المتوقع
        </Typography>
      </Alert>

     
      
      {/* Print Receipt Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PrintIcon />}
          onClick={handlePrintReceipt}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontSize: '1.1rem',
            px: 4,
            py: 2,
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          طباعة الفاتورة
        </Button>
      </Box>
      
      <Alert severity="info" sx={{ mt: 3, textAlign: 'right', backgroundColor: 'white' }}>
        <Typography variant="body2">
          💡 نصيحة: اكتب كود الطلب في مكان آمن أو خذ لقطة شاشة لهذه الصفحة
        </Typography>
      </Alert>
    </Paper>
  );

  if (cartItems.length === 0 && !orderPlaced) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, mt: 8 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
          <CartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
            عربة التسوق فارغة
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            لا توجد منتجات في عربة التسوق للمتابعة إلى الدفع
          </Typography>
          <Button variant="contained" onClick={() => navigate('/store')}>
            تصفح المنتجات
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: 8 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
        إتمام الطلب
      </Typography>
      
      {!orderPlaced && (
        <>
          {/* Mobile Compact Stepper */}
          {isMobile ? (
            <Box sx={{ mb: 4, px: 1 }}>
              <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'text.secondary' }}>
                الخطوة {activeStep + 1} من {steps.length}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                {steps.map((label, index) => (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column',
                      minWidth: '50px',
                      textAlign: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: index === activeStep ? 'primary.main' : 
                                       index < activeStep ? 'success.main' : 'grey.300',
                        color: index <= activeStep ? 'white' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        mb: 0.5
                      }}
                    >
                      {index < activeStep ? '✓' : index + 1}
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.65rem',
                        lineHeight: 1,
                        maxWidth: '45px',
                        wordBreak: 'break-word',
                        color: index === activeStep ? 'primary.main' : 
                               index < activeStep ? 'success.main' : 'text.secondary',
                        fontWeight: index === activeStep ? 'bold' : 'normal'
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            /* Desktop Stepper */
            <Stepper 
              activeStep={activeStep} 
              sx={{ 
                mb: 4,
                '& .MuiStepLabel-label': {
                  fontSize: '0.875rem'
                }
              }}
              alternativeLabel
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </>
      )}
      
      <Box sx={{ mb: 4 }}>
        {renderStepContent(activeStep)}
      </Box>
      
      {!orderPlaced && activeStep < (user ? 4 : 5) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            السابق
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              activeStep === steps.length - 1 ? 'تأكيد الطلب' : 'التالي'
            )}
          </Button>
        </Box>
      )}
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Checkout;
